import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useAuthContext } from "../hooks/useAuthContext";
import { ApiConnectionString } from "../constants/ApiConntection";
import LoadingSpinner from "../components/LoadingSpinner";

export default function LinksSection() {
  const lastSavedLinksRef = useRef({});
  const linkAbortRef = useRef({});
  const [links, setLinks] = useState([]);

  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const { user } = useAuthContext();
  const [isLoading, setIsLoading] = useState(false);
  const [removingId, setRemovingId] = useState(null);
  const [addingLink, setAddingLink] = useState(false);
  const [savingId, setSavingId] = useState(null);

  const fetchData = async () => {
    if (!user) return;

    setIsLoading(true);
    setSubmitError("");
    setSubmitSuccess(false);

    try {
      const response = await axios.get(`${ApiConnectionString}/api/PractitionerLink`, {
        headers: { Authorization: `Bearer ${user.token}` },
      });

      const data = response.data || [];
      setLinks(data);

      lastSavedLinksRef.current = data.reduce((acc, l) => {
        if (l.id) {
          acc[l.id] = {
            title: l.title ?? "",
            url: l.url ?? "",
          };
        }
        return acc;
      }, {});
    } catch (err) {
      setSubmitSuccess(false);
      setSubmitError(err?.response?.data || err?.message || "Something went wrong");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      fetchData();
    }
  }, [user]);

  const updateLink = (index, key, value) => {
    setLinks((prev) => prev.map((l, i) => (i === index ? { ...l, [key]: value } : l)));
  };

  const addLink = async () => {
    if (!user) return;

    setAddingLink(true);
    setSubmitError("");
    setSubmitSuccess(false);

    try {
      const response = await axios.post(`${ApiConnectionString}/api/PractitionerLink`, null, {
        headers: { Authorization: `Bearer ${user.token}` },
      });

      if (response.status === 200) {
        const newLink = {
          title: "",
          url: "",
          id: response.data.id,
          sortOrder: response.data.sortOrder,
        };

        setLinks((prev) => [...prev, newLink]);

        if (newLink.id) {
          lastSavedLinksRef.current = {
            ...lastSavedLinksRef.current,
            [newLink.id]: { title: "", url: "" },
          };
        }

        setSubmitSuccess(true);
      }
    } catch (err) {
      setSubmitSuccess(false);
      setSubmitError(err?.response?.data || err?.message || "Something went wrong");
    } finally {
      setAddingLink(false);
    }
  };

  const removeLink = async (index) => {
    if (!user) return;

    const target = links[index];
    if (!target) return;

    setSubmitError("");
    setSubmitSuccess(false);

    if (!target.id) {
      setLinks((prev) => prev.filter((_, i) => i !== index));
      return;
    }

    setRemovingId(target.id);
    try {
      await axios.delete(`${ApiConnectionString}/api/PractitionerLink/${target.id}`, {
        headers: { Authorization: `Bearer ${user.token}` },
      });

      setLinks((prev) => prev.filter((_, i) => i !== index));

      const copy = { ...lastSavedLinksRef.current };
      delete copy[target.id];
      lastSavedLinksRef.current = copy;

      setSubmitSuccess(true);
      setSubmitError("");
    } catch (err) {
      setSubmitSuccess(false);
      setSubmitError(err?.response?.data || err?.message || "Something went wrong");
    } finally {
      setRemovingId(null);
    }
  };

  const saveLinkOnBlur = async (linkId) => {
    if (!user) return;
    if (!linkId) return;

    const current = links.find((x) => x.id === linkId);
    if (!current) return;

    const prev = lastSavedLinksRef.current[linkId] || {};
    const next = {
      title: current.title ?? "",
      url: current.url ?? "",
    };

    const changed = next.title !== (prev.title ?? "") || next.url !== (prev.url ?? "");
    if (!changed) return;

    if (linkAbortRef.current[linkId]) {
      linkAbortRef.current[linkId].abort();
    }
    linkAbortRef.current[linkId] = new AbortController();

    setSavingId(linkId);
    setSubmitError("");
    setSubmitSuccess(false);

    try {
      await axios.put(
        `${ApiConnectionString}/api/PractitionerLink/${linkId}`,
        { Title: next.title, Url: next.url },
        {
          headers: { Authorization: `Bearer ${user.token}` },
          signal: linkAbortRef.current[linkId].signal,
        }
      );

      lastSavedLinksRef.current = {
        ...lastSavedLinksRef.current,
        [linkId]: next,
      };

      setSubmitSuccess(true);
      setSubmitError("");
    } catch (err) {
      const aborted =
        err?.name === "CanceledError" || err?.name === "AbortError" || err?.code === "ERR_CANCELED";

      if (aborted) return;

      setSubmitSuccess(false);
      setSubmitError(err?.response?.data || err?.message || "Something went wrong");
    } finally {
      setSavingId(null);
    }
  };

  if (isLoading) {
    return (
      <div>
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <>
      <div className="mb-3">
        <div className="text-muted">Add any links you would like shown on your site.</div>
        <div className="text-muted small">We save each link when you leave a field.</div>
      </div>

      {submitError ? <div className="alert alert-danger">{submitError}</div> : null}
      {submitSuccess ? <div className="alert alert-success">Saved</div> : null}

      {links.length === 0 ? (
        <div className="border rounded p-3 text-muted">
          No links added yet. Press Add link to create your first one.
        </div>
      ) : null}

      <div className="d-grid gap-3">
        {links.map((l, i) => (
          <div key={l.id || i} className="card border-0 shadow-sm">
            <div className="card-body">
              <div className="row g-3 align-items-start">
                <div className="col-12 col-md-5">
                  <label className="form-label">Link title</label>
                  <div className="form-text">For example Instagram, Booking link, Counselling Directory.</div>
                  <input
                    className="form-control"
                    required
                    value={l.title ?? ""}
                    onChange={(e) => updateLink(i, "title", e.target.value)}
                    onBlur={() => saveLinkOnBlur(l.id)}
                    placeholder="For example Instagram"
                    disabled={removingId === l.id}
                  />
                  <div className="invalid-feedback">Please fill this in or type NA</div>
                </div>

                <div className="col-12 col-md-7">
                  <label className="form-label">Url</label>
                  <div className="form-text">Copy and paste the full link, starting with https.</div>
                  <input
                    className="form-control"
                    required
                    value={l.url ?? ""}
                    onChange={(e) => updateLink(i, "url", e.target.value)}
                    onBlur={() => saveLinkOnBlur(l.id)}
                    placeholder="For example https://example.com"
                    disabled={removingId === l.id}
                  />
                  <div className="invalid-feedback">Please fill this in or type NA</div>
                </div>

                <div className="col-12 d-flex gap-2">
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    onClick={() => removeLink(i)}
                    disabled={removingId === l.id}
                  >
                    {removingId === l.id ? "Removing" : "Remove"}
                  </button>

                  {savingId === l.id ? (
                    <div className="text-muted small align-self-center">Saving</div>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="d-flex justify-content-end mt-3">
        <button
          type="button"
          className="btn btn-outline-primary btn-sm"
          onClick={addLink}
          disabled={addingLink}
        >
          {addingLink ? "Adding" : "Add link"}
        </button>
      </div>
    </>
  );
}
